import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon } from '@codex-brightworld/icons';

const StyledUlList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
`;

const StyledListItem = styled.li`
  display: inline;
  margin-right: 8px;
  
  &:last-child {
    margin-right: 0px;
  }
`;

const StyledPageNumber = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 6px 0 7px;
  text-decoration: none;
  width: 32px;
  height:32px;
  font-size: 14px;
  line-height: 1.36;

  ${({ theme }) => css`
    color: ${theme.colors.primary.default};
    background-color: ${theme.colors.shades[7]};
    border-width:2px;
    border-style: solid;
    border-color: ${theme.colors.pageAnchor.border};
    border-radius: 50%;
  `}
  &:hover {
      color: ${({ theme }) => theme.colors.link.default};
      border-color:${({ theme }) => theme.colors.pageAnchor.border};
      background-color: #f9fbfc;
  }
`;

const StyledCurrentPageNumber = styled(StyledPageNumber)`
  font-weight: bold;
  
  ${({ theme }) => css`
    background-color: ${theme.colors.primary.default};
    color: ${theme.colors.shades[7]};
    border-color: ${theme.colors.primary.default};
  `}
`;

const Paginator = (props) => {
  const onLinkClick = (e, page) => {
    e.preventDefault();
    props.onLinkClick(page.url, page.label);
  };

  const getCurrentPageIndex = () => props.pagesList.findIndex(page => !!page.isCurrent);

  const createAnchor = (page) => {
    const {
      url, anchorLabel, isCurrent, title
    } = page;
    if (isCurrent) {
      return (
        <StyledCurrentPageNumber title={title}>
          {anchorLabel}
        </StyledCurrentPageNumber>
      );
    }
    return (
      <StyledPageNumber
        onClick={e => onLinkClick(e, page)}
        href={url}
        title={title}
      >
        {anchorLabel}
      </StyledPageNumber>
    );
  };

  const {
    renderArrowLeft,
    renderArrowRight,
    pagesList,
    leftArrow,
    rightArrow,
    renderPageLink
  } = props;

  return (
    <StyledUlList data-qa={props.qa}>
      {renderArrowLeft(leftArrow, pagesList, getCurrentPageIndex, createAnchor)}
      {pagesList.map(item => renderPageLink(item, createAnchor))}
      {renderArrowRight(rightArrow, pagesList, getCurrentPageIndex, createAnchor)}
    </StyledUlList>
  );
};

Paginator.displayName = 'Paginator';

Paginator.propTypes = {
  pagesList: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    label: PropTypes.number,
    isCurrent: PropTypes.bool
  })),
  onLinkClick: PropTypes.func,
  /**
   * @param {string} arrow
   * @param {array} pagesList
   * @param {function} getCurrentPageIndex
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderArrowLeft: PropTypes.func,
  /**
   * @param {string} arrow
   * @param {array} pagesList
   * @param {function} getCurrentPageIndex
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderArrowRight: PropTypes.func,
  /**
   * @param {object} page
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderPageLink: PropTypes.func,
  leftArrow: PropTypes.node,
  rightArrow: PropTypes.node,
  qa: PropTypes.string
};

Paginator.defaultProps = {
  pagesList: [],
  onLinkClick: () => {},
  renderArrowLeft: (arrow, pagesList, getCurrentPageIndex, createAnchor) => {
    const previousPage = pagesList[getCurrentPageIndex() - 1];

    if (previousPage) {
      return (
        <StyledListItem key="previous">
          {createAnchor({ ...previousPage, anchorLabel: arrow, title: 'previous' })}
        </StyledListItem>
      );
    }
    return null;
  },
  renderArrowRight: (arrow, pagesList, getCurrentPageIndex, createAnchor) => {
    const nextPage = pagesList[getCurrentPageIndex() + 1];

    if (nextPage) {
      return (
        <StyledListItem key="next">
          {createAnchor({ ...nextPage, anchorLabel: arrow, title: 'next' })}
        </StyledListItem>
      );
    }
    return null;
  },
  renderPageLink: (page, createAnchor) => (
    <StyledListItem key={page.label}>
      {createAnchor({ ...page, anchorLabel: page.label, title: page.label })}
    </StyledListItem>
  ),
  leftArrow: <Icon icon="arrowleft" />,
  rightArrow: <Icon icon="arrowright" />,
  qa: undefined
};

export default Paginator;
